import React, { useMemo, useRef, useState } from "react";
import {
  FaPassport,
  FaDollarSign,
  FaStethoscope,
  FaFileAlt,
  FaIdCard,
} from "react-icons/fa";
import {
  uploadDocument,
  deleteDocument,
  type RequirementStatus,
  type UserDocument,
} from "../../API/documentAPI";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useConfirmDialog } from "../../components/useConfirmDialog";

// ---------------------------------------------------------------------------
// RequirementCard — single requirement with files nested under it.
//
// Upload mechanics:
//   - File picker accepts multiple files (one shot or one at a time both work)
//   - We respect maxCount: silently slice the picker selection to the remaining
//     room and surface a warning if the user picked too many
//   - Users remove a file and upload another (no Replace action)
//   - Remove is enabled for status === 'uploaded' | 'needs_reupload' | 'rejected'
// ---------------------------------------------------------------------------

interface RequirementCardProps {
  requirement: RequirementStatus;
  /** Documents FK'd to this requirement, in upload order (parent slices). */
  documents: UserDocument[];
  applicationId: number;
  /** Refresh hook — call after any upload / delete succeeds. */
  onChange: () => void;
  /**
   * Optional override for "View" clicks. Lets pages that own a rich preview
   * modal intercept; default falls back to window.open
   * with the document's signed downloadUrl.
   */
  onView?: (doc: UserDocument) => void;
  disabled?: boolean;
  disabledReason?: string;
}

type FileChip = {
  bg: string;
  text: string;
  label: "Uploaded" | "In review" | "Approved" | "Needs reupload";
};

function fileStatusChip(status: UserDocument["status"]): FileChip {
  switch (status) {
    case "approved":
      return { bg: "bg-[#DCFCE7]", text: "text-[#16A34A]", label: "Approved" };
    case "under_review":
      return { bg: "bg-[#DBEAFE]", text: "text-[#1D4ED8]", label: "In review" };
    case "rejected":
    case "needs_reupload":
      return { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", label: "Needs reupload" };
    case "uploaded":
    default:
      return { bg: "bg-[#FEF9C3]", text: "text-[#A16207]", label: "Uploaded" };
  }
}

function canUserRemove(status: UserDocument["status"]): boolean {
  return (
    status === "uploaded" ||
    status === "needs_reupload" ||
    status === "rejected"
  );
}

function getCategoryIcon(requirement: RequirementStatus) {
  const description = requirement.description?.toLowerCase() || "";
  const iconClass = "text-[#16A34A] text-[24px]";
  if (description.includes("passport")) return <FaPassport className={iconClass} />;
  if (
    description.includes("medical") ||
    description.includes("i-693") ||
    description.includes("doctor")
  ) {
    return <FaStethoscope className={iconClass} />;
  }
  if (
    description.includes("financial") ||
    description.includes("tax") ||
    description.includes("pay stub") ||
    description.includes("bank")
  ) {
    return <FaDollarSign className={iconClass} />;
  }
  switch (requirement.category) {
    case "identity":
      return <FaIdCard className={iconClass} />;
    case "supporting":
      return <FaDollarSign className={iconClass} />;
    case "uscis_form":
      return <FaPassport className={iconClass} />;
    case "employment":
    case "education":
    default:
      return <FaFileAlt className={iconClass} />;
  }
}

function formatBytes(n?: number | null): string | null {
  if (!n) return null;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

function mimeShortLabel(mt: string): string {
  const map: Record<string, string> = {
    "application/pdf": "PDF",
    "image/jpeg": "JPEG",
    "image/jpg": "JPEG",
    "image/png": "PNG",
    "image/webp": "WebP",
    "image/tiff": "TIFF",
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  };
  return map[mt] ?? mt;
}

function parseTitleAndSubtitle(description: string): { title: string; subtitle: string } {
  const desc = description?.trim() || "";
  const hasParens = desc.includes("(") && desc.includes(")");
  if (hasParens) {
    return {
      title: desc.split("(")[0].trim(),
      subtitle: desc.slice(desc.indexOf("(") + 1, desc.lastIndexOf(")")).trim(),
    };
  }
  const sep = desc.match(/(.+)\s[-–—]\s(.+)/);
  if (sep) return { title: sep[1].trim(), subtitle: sep[2].trim() };
  return { title: desc, subtitle: "" };
}

const RequirementCard: React.FC<RequirementCardProps> = ({
  requirement,
  documents,
  applicationId,
  onChange,
  onView,
  disabled = false,
  disabledReason,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { confirm, dialogProps } = useConfirmDialog();
  const isBusy = disabled || uploading || removingId !== null;

  const slotMode = Boolean(
    (requirement.slots && requirement.slots.length > 0) ||
      (requirement.slotLabels && requirement.slotLabels.length > 0)
  );

  const slotRows = useMemo(() => {
    if (requirement.slots && requirement.slots.length > 0) {
      return requirement.slots.map((slot) => ({
        key: slot.key,
        label: slot.label,
        doc: documents.find((d) => d.slotKey === slot.key) ?? null,
      }));
    }
    if (requirement.slotLabels && requirement.slotLabels.length > 0) {
      return requirement.slotLabels.map((label, index) => {
        const key =
          label
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "") || `slot_${index + 1}`;
        return {
          key,
          label,
          doc: documents.find((d) => d.slotKey === key) ?? null,
        };
      });
    }
    return [];
  }, [requirement.slots, requirement.slotLabels, documents]);

  const pendingSlotKeyRef = useRef<string | null>(null);

  const { title, subtitle } = useMemo(
    () => parseTitleAndSubtitle(requirement.description),
    [requirement.description]
  );

  const slotsRemaining = useMemo(() => {
    if (requirement.maxCount == null) return Infinity;
    return Math.max(0, requirement.maxCount - documents.length);
  }, [requirement.maxCount, documents.length]);

  const acceptAttr = useMemo(() => {
    if (requirement.acceptedMimeTypes && requirement.acceptedMimeTypes.length > 0) {
      return requirement.acceptedMimeTypes.join(",");
    }
    return ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.tif,.tiff";
  }, [requirement.acceptedMimeTypes]);

  const acceptedLabel = useMemo(() => {
    if (!requirement.acceptedMimeTypes || requirement.acceptedMimeTypes.length === 0) {
      return null;
    }
    return requirement.acceptedMimeTypes.map(mimeShortLabel).join(", ");
  }, [requirement.acceptedMimeTypes]);

  const maxBytesLabel = formatBytes(requirement.maxFileBytes);

  const cardStatus = useMemo<FileChip>(() => {
    if (!requirement.isRequired && documents.length === 0) {
      return { bg: "bg-gray-100", text: "text-gray-500", label: "Uploaded" };
    }
    if (requirement.approvedCount >= requirement.minCount) {
      return { bg: "bg-[#DCFCE7]", text: "text-[#16A34A]", label: "Approved" };
    }
    if (documents.some((d) => d.status === "needs_reupload" || d.status === "rejected")) {
      return { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", label: "Needs reupload" };
    }
    if (documents.some((d) => d.status === "under_review")) {
      return { bg: "bg-[#DBEAFE]", text: "text-[#1D4ED8]", label: "In review" };
    }
    if (documents.length > 0) {
      return { bg: "bg-[#FEF9C3]", text: "text-[#A16207]", label: "Uploaded" };
    }
    return { bg: "bg-gray-100", text: "text-gray-700", label: "Uploaded" };
  }, [requirement, documents]);

  const isAwaitingFirstUpload = documents.length === 0;

  const handleSlotUploadClick = (slotKey: string) => {
    if (isBusy) return;
    pendingSlotKeyRef.current = slotKey;
    fileInputRef.current?.click();
  };

  const handleSlotFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isBusy) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      pendingSlotKeyRef.current = null;
      return;
    }
    const file = event.target.files?.[0];
    const slotKey = pendingSlotKeyRef.current;
    if (!file || !slotKey) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      pendingSlotKeyRef.current = null;
      return;
    }
    setError(null);
    setUploading(true);
    try {
      await uploadDocument(
        applicationId,
        requirement.requirementId,
        file,
        requirement.category,
        requirement.description,
        undefined,
        slotKey
      );
      onChange();
    } catch (err: any) {
      setError(err?.message || "Failed to upload file");
    } finally {
      setUploading(false);
      pendingSlotKeyRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isBusy) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    setError(null);

    let toUpload = files;
    if (Number.isFinite(slotsRemaining) && files.length > slotsRemaining) {
      toUpload = files.slice(0, slotsRemaining);
      setError(
        `Only ${slotsRemaining} more file(s) allowed for this requirement; the extra were skipped.`
      );
    }
    if (toUpload.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      await Promise.all(
        toUpload.map((file) =>
          uploadDocument(
            applicationId,
            requirement.requirementId,
            file,
            requirement.category,
            requirement.description
          )
        )
      );
      onChange();
    } catch (err: any) {
      setError(err?.message || "Failed to upload one or more files");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemove = async (doc: UserDocument) => {
    if (isBusy) return;
    if (!canUserRemove(doc.status)) {
      setError(
        "This file has been reviewed and can't be removed while review is in progress or after approval."
      );
      return;
    }
    const ok = await confirm({
      title: "Remove this document?",
      message: `Remove “${doc.fileName}”? You can upload another file afterward.`,
      confirmLabel: "Remove",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!ok) return;

    setError(null);
    setRemovingId(doc.id);
    try {
      await deleteDocument(applicationId, doc.id);
      onChange();
    } catch (err: any) {
      setError(err?.message || "Failed to remove file");
    } finally {
      setRemovingId(null);
    }
  };

  const handleView = (doc: UserDocument) => {
    if (onView) {
      onView(doc);
      return;
    }
    setError("Preview is unavailable on this page.");
  };

  const renderDocActions = (doc: UserDocument) => {
    const isRemoving = removingId === doc.id;
    return (
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => handleView(doc)}
          className="text-xs text-[#1B365D] hover:underline"
        >
          View
        </button>
        {canUserRemove(doc.status) && (
          <button
            type="button"
            disabled={isBusy}
            onClick={() => void handleRemove(doc)}
            className="text-xs text-red-600 hover:underline disabled:opacity-50"
          >
            {isRemoving ? "Removing…" : "Remove"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className="border border-[#E5E7EB] px-6 py-5 rounded-[16px] mb-4 bg-white"
      data-requirement-id={requirement.requirementId}
    >
      {dialogProps ? <ConfirmDialog {...dialogProps} /> : null}

      <div className="flex justify-between items-start gap-4">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <div className="flex items-center justify-center pt-1">
            {getCategoryIcon(requirement)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[18px] font-semibold text-[#1B365D] truncate">
                {title || requirement.description}
              </p>
              {subtitle && (
                <span className="text-[16px] text-[#6B7280]">({subtitle})</span>
              )}
              {requirement.source === "conditional" && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                  Based on your answers
                </span>
              )}
              {!requirement.isRequired && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                  Optional
                </span>
              )}
            </div>
            {requirement.hintText && (
              <p className="text-sm text-[#6B7280] mt-1">{requirement.hintText}</p>
            )}
            {(acceptedLabel || maxBytesLabel) && (
              <p className="text-xs text-[#9CA3AF] mt-1">
                {acceptedLabel ? `Accepts: ${acceptedLabel}` : "Accepts: PDF, JPG, PNG, WebP, DOC"}
                {maxBytesLabel ? ` · up to ${maxBytesLabel}` : ""}
              </p>
            )}
          </div>
        </div>

        {requirement.minCount > 0 && (
          <span
            className={`text-sm font-medium py-1 px-4 rounded-[50px] ${cardStatus.bg} ${cardStatus.text} shrink-0`}
          >
            {requirement.approvedCount} of {requirement.minCount} approved
          </span>
        )}
      </div>

      {disabled && disabledReason ? (
        <p className="mt-4 text-sm text-[#6B7280] bg-[#F9FAFB] border border-[#E5E7EB] rounded-[12px] px-4 py-3">
          {disabledReason}
        </p>
      ) : null}

      {slotMode && slotRows.length > 0 ? (
        <ul className="mt-4 space-y-3">
          {slotRows.map((slot) => {
            const doc = slot.doc;
            const chip = doc ? fileStatusChip(doc.status) : null;
            return (
              <li
                key={slot.key}
                className="border border-[#F3F4F6] rounded-[12px] p-3 bg-[#FAFAFA]"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#1B365D]">{slot.label}</p>
                    {doc ? (
                      <>
                        <p className="text-sm font-medium text-[#1B365D] truncate mt-1">
                          {doc.fileName}
                        </p>
                        <p className="text-xs text-[#6B7280] mt-0.5">
                          Uploaded {new Date(doc.createdAt).toLocaleDateString()}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-[#9CA3AF] mt-1">No file uploaded yet</p>
                    )}
                  </div>
                  {doc && chip ? (
                    <span
                      className={`text-xs font-medium py-0.5 px-3 rounded-[50px] ${chip.bg} ${chip.text} shrink-0`}
                    >
                      {chip.label}
                    </span>
                  ) : null}
                  {doc ? (
                    renderDocActions(doc)
                  ) : (
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => handleSlotUploadClick(slot.key)}
                      className="border border-[#1B365D] text-[#1B365D] px-3 py-1.5 rounded-[50px] hover:bg-[#1B365D] hover:text-white transition-colors disabled:opacity-50 text-xs"
                    >
                      {uploading ? "Uploading…" : "Upload"}
                    </button>
                  )}
                </div>
                {doc?.reviewNotes && (
                  <p className="text-xs text-[#991B1B] mt-2 bg-red-50 border border-red-100 rounded p-2">
                    <span className="font-semibold">Admin note: </span>
                    {doc.reviewNotes}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <>
          {documents.length > 0 && (
            <ul className="mt-4 space-y-3">
              {documents.map((doc) => {
                const chip = fileStatusChip(doc.status);
                return (
                  <li
                    key={doc.id}
                    className="border border-[#F3F4F6] rounded-[12px] p-3 bg-[#FAFAFA]"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#1B365D] truncate">
                          {doc.fileName}
                        </p>
                        <p className="text-xs text-[#6B7280] mt-0.5">
                          Uploaded {new Date(doc.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span
                        className={`text-xs font-medium py-0.5 px-3 rounded-[50px] ${chip.bg} ${chip.text} shrink-0`}
                      >
                        {chip.label}
                      </span>
                      {renderDocActions(doc)}
                    </div>
                    {doc.reviewNotes && (
                      <p className="text-xs text-[#991B1B] mt-2 bg-red-50 border border-red-100 rounded p-2">
                        <span className="font-semibold">Admin note: </span>
                        {doc.reviewNotes}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {!slotMode && (slotsRemaining > 0 || !Number.isFinite(slotsRemaining)) && (
            <div className="mt-4">
              <button
                type="button"
                disabled={isBusy}
                onClick={() => fileInputRef.current?.click()}
                className="border border-[#1B365D] text-[#1B365D] px-4 py-2 rounded-[50px] hover:bg-[#1B365D] hover:text-white transition-colors disabled:opacity-50 text-sm"
              >
                {uploading
                  ? "Uploading…"
                  : isAwaitingFirstUpload
                    ? "Upload file"
                    : "Upload another"}
              </button>
            </div>
          )}
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple={!slotMode}
        onChange={slotMode ? handleSlotFileSelected : handleFilesSelected}
        className="hidden"
        accept={acceptAttr}
        disabled={isBusy}
      />

      {error && (
        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}
    </div>
  );
};

export default RequirementCard;
